'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import { ArrowDown, ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type {
  BusinessInfo,
  LandingPageProps,
} from '../_lib/landing-page.types';

/* ───────────────────────── Contact info ─────────────────────────
 * Hardcoded fallbacks for when the tenant DB record doesn't yet have
 * `businessInfo.location` / `contact` populated. The DB values, when
 * present, take precedence everywhere these are used. */
const FALLBACK_DIRECTIONS_URL = 'https://maps.app.goo.gl/SEjzuXdwAbkkTmM5A';
const FALLBACK_EMBED_URL =
  'https://www.google.com/maps?q=PGWF%2BVG9+Diriyah+Saudi+Arabia&output=embed';
const FALLBACK_PHONE = '+966554426646';

/**
 * Derive the iframe src for a Google Maps embed.
 * - If the URL already looks embeddable (`output=embed` or `/maps/embed`),
 *   use it as-is.
 * - Anything else (incl. `maps.app.goo.gl` short links, which don't render
 *   inside an iframe) falls back to the static embed query.
 */
function resolveEmbedUrl(url: string | undefined): string {
  if (!url) return FALLBACK_EMBED_URL;
  if (url.includes('output=embed') || url.includes('/maps/embed')) return url;
  return FALLBACK_EMBED_URL;
}
import './styles.css';

/* ───────────────────────── Section reveal hook ───────────────────────── */

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          node.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

/* ───────────────────────── Brand pattern primitives ─────────────────────────
 * Inline SVGs so the geometry can be colored via CSS / Tailwind classes
 * (text-white, text-[var(--net-lime)], etc.) and reused everywhere.
 *
 * BoomerangShot — one of FIVE individual boomerang shots from the brand
 *   "ball-paths" pattern. Each shot is a single arcing trajectory with
 *   a lime ball dot at the starting terminus. Different sections use
 *   different `shot` indexes (1–5) to keep variety without stacking
 *   the wallpaper. Sourced from /brand-extracted/pattern-ball-paths.svg.
 *
 * BoomerangSweep — a single bold arcing curve with one lime dot, used
 *   exactly once on the page (hero) so it reads as an icon, not wallpaper.
 *   Inspired by the brand "Boomerang shots" application page.
 *
 * CourtFrame — the white tennis-court line geometry from the brand's
 *   "Applications" page, used as a hero frame around content.
 */

type PatternProps = {
  className?: string;
  /** Color of the curving trajectory line. Default = currentColor */
  lineColor?: string;
  /** Color of the lime dot accents. Default = lime brand variable */
  dotColor?: string;
};

/* Five individual boomerang shots from the brand pattern.
 * Each entry: the SVG path (the trajectory) and the [cx, cy] of its
 * lime ball dot. Source viewBox: 65 240 → 470 625. */
const BOOMERANG_SHOTS: ReadonlyArray<{ d: string; cx: number; cy: number }> = [
  {
    d: 'M 384.464844 293.773438 C 374.371094 276.492188 356.414062 264.378906 331.097656 257.765625 C 309.207031 252.046875 281.597656 250.351562 249.035156 252.730469 C 192.902344 256.824219 133.097656 272.09375 92.808594 284.179688 L 88.792969 285.382812 L 86.378906 277.347656 L 90.398438 276.144531 C 131.089844 263.9375 191.535156 248.511719 248.425781 244.359375 C 318.523438 239.242188 365.375 252.445312 387.851562 283.613281 C 394.027344 273.636719 407.714844 255.125 424.269531 255.125 C 424.363281 255.125 424.453125 255.125 424.546875 255.128906 C 436.59375 255.28125 447.257812 264.972656 456.238281 283.9375 L 458.035156 287.730469 L 450.453125 291.324219 L 448.65625 287.53125 C 441.164062 271.710938 433.019531 263.628906 424.449219 263.515625 C 424.390625 263.515625 424.328125 263.515625 424.269531 263.515625 C 410.140625 263.515625 396.074219 285.167969 391.828125 293.550781 Z',
    cx: 76.316,
    cy: 284.75,
  },
  {
    d: 'M 341.933594 375.1875 L 333.835938 374.882812 C 329.988281 358.097656 321.265625 345.882812 307.175781 337.542969 C 286.207031 325.132812 252.941406 321.820312 208.304688 327.695312 C 170.378906 332.6875 130.355469 343.253906 92.75 353.929688 L 88.714844 355.074219 L 86.421875 347 L 90.460938 345.859375 C 128.367188 335.097656 168.753906 324.441406 207.210938 319.378906 C 254.410156 313.164062 288.503906 316.742188 311.445312 330.320312 C 324.457031 338.023438 333.695312 348.992188 339.011719 363.019531 C 341.183594 359.886719 344.191406 356.285156 348.289062 352.664062 C 358.3125 343.804688 377.210938 332.984375 410.304688 331.734375 L 414.5 331.578125 L 414.816406 339.960938 L 410.621094 340.121094 C 386.671875 341.023438 367.195312 347.402344 354.289062 358.5625 C 344.640625 366.90625 341.957031 375.105469 341.933594 375.1875 Z',
    cx: 76.316,
    cy: 353.609,
  },
  {
    d: 'M 350.585938 453.601562 L 342.578125 453.207031 C 334.109375 420.03125 318.648438 398.414062 295.3125 387.132812 C 272.144531 375.929688 241.558594 375.171875 201.804688 384.8125 C 165.585938 393.597656 126.636719 410 93.316406 424.886719 L 89.484375 426.59375 L 86.0625 418.933594 L 89.894531 417.226562 C 123.5625 402.183594 162.953125 385.601562 199.828125 376.660156 C 241.546875 366.542969 273.976562 367.496094 298.964844 379.578125 C 311.285156 385.535156 321.824219 394.332031 330.28125 405.722656 C 337.347656 415.238281 343.125 426.792969 347.519531 440.167969 C 354.402344 427.0625 368.976562 405.347656 393.570312 395.726562 L 397.476562 394.195312 L 400.535156 402.011719 L 396.628906 403.539062 C 364.550781 416.089844 350.722656 453.226562 350.585938 453.601562 Z',
    cx: 76.316,
    cy: 426.925,
  },
  {
    d: 'M 372.691406 539.113281 L 368.535156 538.9375 C 333.664062 517.042969 216.066406 464.910156 92.320312 486.34375 L 88.1875 487.0625 L 86.753906 478.792969 L 90.886719 478.078125 C 122.378906 472.625 155.511719 471.402344 189.363281 474.453125 C 217.347656 476.972656 245.964844 482.410156 274.421875 490.609375 C 320.328125 503.839844 355.722656 521.398438 371 530.605469 C 378.785156 526.949219 406.964844 514.851562 451.785156 508.167969 L 455.933594 507.546875 L 457.171875 515.84375 L 453.023438 516.464844 C 402.425781 524.011719 372.980469 538.964844 372.691406 539.113281 Z',
    cx: 76.316,
    cy: 485.031,
  },
  {
    d: 'M 385.769531 610.140625 L 379.351562 609.269531 C 362.304688 581.710938 318.910156 558.457031 263.265625 547.0625 C 205.40625 535.210938 143.300781 537.617188 92.875 553.660156 L 88.878906 554.933594 L 86.335938 546.9375 L 90.332031 545.664062 C 142.082031 529.199219 205.726562 526.710938 264.949219 538.84375 C 292.738281 544.535156 318.347656 553.324219 339.015625 564.269531 C 359.15625 574.933594 374.21875 587.238281 383.863281 600.894531 C 392.773438 594.207031 417.140625 578.953125 456.148438 576.949219 L 460.335938 576.734375 L 460.765625 585.113281 L 456.578125 585.328125 C 410.492188 587.695312 386.011719 609.914062 385.769531 610.140625 Z',
    cx: 76.316,
    cy: 553.953,
  },
];

/**
 * BoomerangShot — renders a SINGLE boomerang shot from the brand pattern.
 * `shot` selects one of the five trajectories (1–5). Each one has its
 * own arc + lime ball dot at the starting terminus. Use one shot per
 * section so the brand reads as motion, not wallpaper.
 */
function BoomerangShot({
  className = '',
  lineColor = 'currentColor',
  dotColor = 'var(--net-lime)',
  shot = 1,
}: PatternProps & { shot?: 1 | 2 | 3 | 4 | 5 }) {
  const data = BOOMERANG_SHOTS[shot - 1] ?? BOOMERANG_SHOTS[0]!;
  return (
    <svg
      className={className}
      viewBox="65 240 405 90"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <g transform={`translate(0, ${280 - data.cy})`}>
        <path d={data.d} fill={lineColor} fillRule="nonzero" />
        <circle cx={data.cx} cy={data.cy} r="7" fill={dotColor} />
      </g>
    </svg>
  );
}

/**
 * BoomerangSweep — a single bold low-arcing curve like the brand's
 * "Boomerang shots are the best." application. Renders a deep-blue
 * stroke that bounces low across the frame with a lime dot at the
 * starting terminus.
 */
function BoomerangSweep({
  className = '',
  strokeColor = 'var(--net-blue-deep)',
  dotColor = 'var(--net-lime)',
  strokeWidth = 22,
}: {
  className?: string;
  strokeColor?: string;
  dotColor?: string;
  strokeWidth?: number;
}) {
  // Inner-padding the path so the lime dot sits clear of the SVG's left
  // edge (and any framing it overlaps in context). Dot radius is
  // ~strokeWidth * 1.4, so we offset the start x by 2× that radius.
  const startX = Math.round(strokeWidth * 2.8);
  return (
    <svg
      className={className}
      viewBox="0 0 1600 360"
      preserveAspectRatio="none"
      aria-hidden
      fill="none"
    >
      {/* Bold sweeping arc — rises, dives, rises again */}
      <path
        d={`M ${startX} 240 C 260 80, 600 80, 880 240 S 1380 380, 1570 240`}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      {/* Lime dot at the leading edge */}
      <circle cx={startX} cy="240" r={strokeWidth * 1.4} fill={dotColor} />
    </svg>
  );
}

/**
 * CourtFrame — the white tennis-court line geometry from the brand's
 * "Applications" page. Used as a hero frame around content.
 *   - Outer doubles rectangle
 *   - Service-line top/bottom, center service line, and net line
 * Stroke uses currentColor so it can be themed (white on blue, ink on chalk).
 */
function CourtFrame({
  className = '',
  strokeWidth = 2,
}: {
  className?: string;
  strokeWidth?: number;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 1200 700"
      preserveAspectRatio="none"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
    >
      {/* Outer doubles court */}
      <rect x="20" y="20" width="1160" height="660" />
      {/* Top sideline → singles court */}
      <line x1="120" y1="20" x2="120" y2="680" />
      <line x1="1080" y1="20" x2="1080" y2="680" />
      {/* Service lines */}
      <line x1="120" y1="200" x2="1080" y2="200" />
      <line x1="120" y1="500" x2="1080" y2="500" />
      {/* Center service line */}
      <line x1="600" y1="200" x2="600" y2="500" />
      {/* Net line (vertical center) */}
      <line x1="600" y1="20" x2="600" y2="200" />
      <line x1="600" y1="500" x2="600" y2="680" />
    </svg>
  );
}

/* ───────────────────────── Section: Hero ───────────────────────── */

function HeroSection() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <section
      id="top"
      className="relative isolate overflow-hidden bg-[var(--net-blue)] text-white"
    >
      {/* Court frame — white lines, like the brand "Applications" page */}
      <CourtFrame
        className="net-rise pointer-events-none absolute inset-x-6 top-24 bottom-10 text-white/35 sm:inset-x-10 lg:inset-x-14"
        strokeWidth={2}
      />

      {/* The bold boomerang sweep curving low across the court — desktop only.
       * On mobile the SVG's stretched aspect ratio makes the lime dot squash
       * over the CTAs, so we hide it below lg. The path itself reserves
       * inner padding for the lime ball so it doesn't clip on edges. */}
      <BoomerangSweep
        className="net-rise pointer-events-none absolute inset-x-14 bottom-32 hidden h-[28vh] lg:block"
        strokeColor="var(--net-blue-deep)"
        dotColor="var(--net-lime)"
        strokeWidth={26}
      />

      <div className="relative mx-auto flex min-h-[100vh] max-w-[1440px] flex-col px-6 pb-16 pt-36 sm:px-10 sm:pt-40 lg:px-14 lg:pt-44">
        {/* Massive headline. Arabic gets a softer line-height because its
         * glyphs have taller diacritics that collide at 0.85 leading. */}
        <h1
          className={cn(
            'net-rise font-net-display max-w-5xl tracking-tight',
            'text-[12vw] sm:text-[11vw] lg:text-[9.5rem]',
            isAr ? 'leading-[1.05]' : 'leading-[0.85]',
          )}
          style={{ animationDelay: '0.15s' }}
        >
          {isAr ? (
            <>
              <span className="block pb-1">حيث</span>
              <span className="block pb-1">يجتمع</span>
              <span className="block text-[var(--net-lime)]">التنس</span>
            </>
          ) : (
            <>
              <span className="block">Where</span>
              <span className="block text-[var(--net-lime)]">Tennis</span>
              <span className="block">comes</span>
              <span className="block">together</span>
            </>
          )}
        </h1>

        {/* Subhead + photo inset (mirrors the brand application layout) */}
        <div
          className="net-rise mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end"
          style={{ animationDelay: '0.3s' }}
        >
          <p className="font-net-body max-w-xl text-base leading-relaxed text-white/85 sm:text-lg lg:col-span-7">
            {isAr
              ? 'مجتمع لمحبي التنس — مبتدئين وهواة ومحترفين، مركز تنس متكامل لجميع الأعمار والمستويات، قائم على تدريب منظّم ومسارات تطوير واضحة وبيئة تدريب احترافية.'
              : 'A community for people who love tennis — beginners, amateurs, and pros alike, a complete tennis hub for all ages and levels – built on structured coaching, clear development pathways, and a professional training environment.'}
          </p>

          <div className="flex flex-col gap-4 sm:flex-row lg:col-span-5 lg:justify-end">
            <Link
              href="/book"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[var(--net-lime)] px-8 text-base font-bold text-[var(--net-ink)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
            >
              {isAr ? 'احجز ملعبك' : 'Book a Court'}
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0" />
            </Link>
            <Link
              href="/book/packages"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-full border border-white/40 px-8 text-base font-medium text-white transition-colors hover:border-[var(--net-lime)] hover:text-[var(--net-lime)]"
            >
              {isAr ? 'اشترك في باقة' : 'Purchase a Package'}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
            <Link
              href="/coaches"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-full border border-white/40 px-8 text-base font-medium text-white transition-colors hover:border-[var(--net-lime)] hover:text-[var(--net-lime)]"
            >
              {isAr ? 'احجز مدرب' : 'Book a Coach'}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
          </div>
        </div>

        {/* "making tennis relieving." service-box footer line */}
        <div
          className="net-rise mt-12 flex items-center justify-between gap-6"
          style={{ animationDelay: '0.4s' }}
        >
          <span className="font-net-label text-[10px] text-white/60">
            {isAr ? 'نلعب التنس بطريقة جديدة.' : 'making tennis relieving.'}
          </span>
          <span className="font-net-label text-[10px] text-white/40">
            01 / 06
          </span>
        </div>

        {/* Stats strip */}
        <div
          className="net-rise mt-12 grid grid-cols-2 gap-x-8 gap-y-10 border-t border-white/20 pt-10 lg:grid-cols-4"
          style={{ animationDelay: '0.55s' }}
        >
          {[
            {
              k: '07',
              l: isAr ? 'ملاعب دولية' : 'Intl. courts',
            },
            {
              k: 'ITF',
              l: isAr ? 'مواصفات' : 'Standards',
            },
            {
              k: isAr ? '٢ خاص' : '2 Private',
              l: isAr ? 'ملاعب بخصوصية كاملة للنساء' : 'Women-only courts',
            },
            {
              // Render the arrow inside an LTR span so bidi reordering
              // doesn't flip the visual direction in Arabic. In RTL the
              // surrounding context flows right-to-left, but inside this
              // node "6 → 00" stays left-to-right and the arrow points
              // forward (start → end). Eastern Arabic numerals in AR.
              k: (
                <span dir="ltr" className="inline-block">
                  {isAr ? '٦ → ٠٠' : '6 → 00'}
                </span>
              ),
              l: isAr ? 'يومياً' : 'Open daily',
            },
          ].map((stat) => (
            <div key={stat.l} className="flex flex-col gap-1">
              <span className="font-net-display text-[2.6rem] leading-none text-white">
                {stat.k}
              </span>
              <span className="font-net-label text-[10px] text-white/70">
                {stat.l}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#about"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 transition-colors hover:text-[var(--net-lime)]"
        aria-label="Scroll to about"
      >
        <ArrowDown className="h-6 w-6 animate-bounce" />
      </a>
    </section>
  );
}

/* ───────────────────────── Section: Marquee strip ───────────────────────── */

function MarqueeStrip() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const items = isAr
    ? ['التنس للجميع', 'تدريب احترافي', 'مجتمع نشط', 'سبعة ملاعب', 'كفي شوب', 'الدرعية']
    : [
        'Tennis for everyone',
        'Pro coaching',
        'Active community',
        'Seven courts',
        'In-house café',
        'Diriyah',
      ];

  // Duplicate for seamless loop
  const loop = [...items, ...items, ...items, ...items];

  return (
    <section
      aria-hidden
      className="overflow-hidden border-y-2 border-[var(--net-ink)] bg-[var(--net-lime)] py-5"
    >
      <div className="net-marquee-track flex w-max items-center gap-10 whitespace-nowrap">
        {loop.map((label, i) => (
          <span
            key={`${label}-${i}`}
            className="font-net-display flex items-center gap-10 text-[var(--net-ink)]"
          >
            <span className="text-[28px] leading-none lg:text-[36px]">
              {label}
            </span>
            <span
              className="inline-block h-3 w-3 flex-shrink-0 rounded-full bg-[var(--net-ink)]"
              aria-hidden
            />
          </span>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Section: About ───────────────────────── */

function AboutSection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <section id="about" className="relative overflow-hidden bg-[var(--net-chalk)] py-24 lg:py-32">
      <div
        className="net-court-grid-tight pointer-events-none absolute inset-0"
        aria-hidden
      />

      {/* One boomerang shot drifting in from the right */}
      <BoomerangShot
        shot={2}
        className="pointer-events-none absolute -right-10 top-10 hidden h-28 w-[640px] text-[var(--net-blue)]/15 lg:block"
        dotColor="var(--net-lime)"
      />

      <div
        ref={ref}
        className="net-reveal relative mx-auto grid max-w-[1280px] grid-cols-1 gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:gap-20 lg:px-14"
      >
        <div className="lg:col-span-5">
          <p className="font-net-label mb-6 flex items-center gap-3 text-[10px] text-[var(--net-blue)]">
            <span className="h-px w-10 bg-[var(--net-blue)]" />
            {isAr ? '01 — عن الأكاديمية' : '01 — About'}
          </p>
          <h2 className="font-net-display text-[2.5rem] leading-[0.95] text-[var(--net-ink)] sm:text-[3.5rem] lg:text-[4.5rem]">
            {isAr ? (
              <>
                مجتمع
                <br />
                <span className="text-[var(--net-blue)]">للتنس.</span>
              </>
            ) : (
              <>
                A club for
                <br />
                <span className="text-[var(--net-blue)]">tennis people.</span>
              </>
            )}
          </h2>
        </div>

        <div className="space-y-6 text-[var(--net-ink)] lg:col-span-7 lg:pt-6">
          <p className="font-net-body text-lg leading-relaxed sm:text-xl">
            {isAr
              ? 'تأسست أكاديمية نت للتنس لتكون مجتمعاً لمحبي هذه الرياضة، ولتخدم المبتدئين والهواة والمحترفين.'
              : 'NET Tennis Academy was founded as a community for the people who love this game — built to serve beginners, amateurs, and professionals alike.'}
          </p>
          <p className="font-net-body text-base leading-relaxed text-[var(--net-ink)]/80">
            {isAr
              ? 'تحتوي على سبعة ملاعب بمواصفات دولية، إضافةً إلى مرافق راقية كالغرفة الداخلية للتمارين والإطالات وغرف تبديل ملابس ومراوش وخزائن أمانات للجنسين. كما يوجد شاشة لعرض جميع بطولات التنس وجلسات داخلية وخارجية وكفي شوب. كما أن الملاعب متاحة للتأجير في الأوقات التي لا يكون فيها حصص تدريبية. أيضاً لدينا ملعب بخصوصية كاملة للنساء.'
              : 'Seven international-spec courts plus a full set of premium facilities: an indoor exercise and stretching room, locker rooms, showers, and security lockers for both men and women. A live screen for tournaments, indoor and outdoor lounges, and an in-house café.'}
          </p>

          <ul className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2">
            {(isAr
              ? [
                  'ملاعب معتمدة من الاتحاد الدولي للتنس',
                  'ملعبان خاصان للنساء',
                  'برامج لجميع الأعمار والمستويات',
                  'تأجير الملاعب في الأوقات التي لا يكون فيها حصص تدريبية',
                ]
              : [
                  'ITF approved courts',
                  '2 private women courts',
                  'Programs for every age and level',
                  'Court rental outside class hours',
                ]
            ).map((item) => (
              <li
                key={item}
                className="font-net-body flex items-center gap-3 text-base"
              >
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--net-lime)]">
                  <Check className="h-3.5 w-3.5 text-[var(--net-ink)]" strokeWidth={3} />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: Programs ───────────────────────── */

function ProgramsSection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  const programs = isAr
    ? [
        {
          n: '01',
          title: 'مبتدئون',
          desc: 'تعلّم اللعبة بالطريقة الصحيحة من اليوم الأول، مع التركيز على التعلّم الممتع.',
          tag: 'كل الأعمار',
        },
        {
          n: '02',
          title: 'هواة',
          desc: 'برامج رائعة لتطوير اللاعبين والتواصل الاجتماعي.',
          tag: 'مستوى متوسط',
        },
        {
          n: '03',
          title: 'محترفون',
          desc: 'تدريب عالي الكثافة، تحليل أداء، مهارات تكتيكية وتقنية وذهنية، وإعداد بدني.',
          tag: 'تنافسي',
        },
        {
          n: '04',
          title: 'برنامج النساء',
          desc: 'حصص في ملاعب بخصوصية كاملة للنساء، بيئة آمنة ومريحة.',
          tag: 'خاص',
        },
        {
          n: '05',
          title: 'الناشئون',
          desc: 'نظام متكامل لتطوير الناشئين مصمم لبناء لاعبين أذكياء ومرنين وقادرين على التكيّف — يجمع بين الأسس البدنية واتخاذ القرار والوعي التكتيكي ضمن مسار منظّم.',
          tag: 'تحت 16',
        },
        {
          n: '06',
          title: 'تأجير الملاعب',
          desc: 'احجز ملعبك في الأوقات التي لا يكون فيها حصص تدريبية.',
          tag: 'مفتوح',
        },
      ]
    : [
        {
          n: '01',
          title: 'Beginners',
          desc: 'Learn the game the right way from day one, with focus on fun learning.',
          tag: 'All ages',
        },
        {
          n: '02',
          title: 'Amateurs',
          desc: 'Great programs for player development and socializing.',
          tag: 'Intermediate',
        },
        {
          n: '03',
          title: 'Pros',
          desc: 'High-intensity training, performance analysis, tactical, technical, mental skills and physical conditioning.',
          tag: 'Competitive',
        },
        {
          n: '04',
          title: 'Women’s program',
          desc: 'Private classes on our women-only courts — calm, focused, comfortable.',
          tag: 'Private',
        },
        {
          n: '05',
          title: 'Juniors',
          desc: 'A complete junior development system designed to build intelligent, resilient, adaptable players — combining athletic foundations, decision-making, and tactical awareness within a structured pathway.',
          tag: 'U16',
        },
        {
          n: '06',
          title: 'Court rental',
          desc: 'Book any court outside training hours — solo, doubles, or groups.',
          tag: 'Open',
        },
      ];

  return (
    <section
      id="programs"
      className="relative overflow-hidden bg-[var(--net-ink)] py-24 text-white lg:py-32"
    >
      <div className="net-court-grid pointer-events-none absolute inset-0 opacity-40" aria-hidden />

      {/* One boomerang shot streaking behind the title */}
      <BoomerangShot
        shot={3}
        className="pointer-events-none absolute -left-20 top-16 h-32 w-[560px] text-[var(--net-lime)]/20 sm:top-12 sm:w-[720px]"
        dotColor="var(--net-lime)"
      />

      <div ref={ref} className="net-reveal relative mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-14">
        <div className="mb-16 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-net-label mb-6 flex items-center gap-3 text-[10px] text-[var(--net-lime)]">
              <span className="h-px w-10 bg-[var(--net-lime)]" />
              {isAr ? '02 — البرامج' : '02 — Programs'}
            </p>
            <h2 className="font-net-display text-[2.5rem] leading-[0.95] sm:text-[3.5rem] lg:text-[5rem]">
              {isAr ? (
                <>
                  كل المستويات.
                  <br />
                  <span className="text-[var(--net-lime)]">جميع الأعمار.</span>
                </>
              ) : (
                <>
                  Every level.
                  <br />
                  <span className="text-[var(--net-lime)]">Every age.</span>
                </>
              )}
            </h2>
          </div>
          <p className="font-net-body max-w-md text-base leading-relaxed text-white/70">
            {isAr
              ? 'برامجنا التدريبية للرجال والنساء والأطفال على جميع المستويات. اختر ما يناسبك.'
              : 'Our programs are built for men, women and kids across every level. Pick the one that fits where you are.'}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {programs.map((p, idx) => (
            <article
              key={p.n}
              className="group relative bg-[var(--net-ink)] p-8 transition-colors duration-500 hover:bg-[var(--net-blue)] lg:p-10"
            >
              <div className="flex items-start justify-between">
                <span className="font-net-display text-[2.5rem] leading-none text-white/30 transition-colors group-hover:text-[var(--net-lime)]">
                  {p.n}
                </span>
                <span className="font-net-label rounded-full border border-white/20 px-3 py-1 text-[9px] text-white/70">
                  {p.tag}
                </span>
              </div>
              <h3 className="font-net-display mt-12 text-[1.75rem] leading-tight text-white lg:text-[2rem]">
                {p.title}
              </h3>
              <p className="font-net-body mt-3 text-sm leading-relaxed text-white/70">
                {p.desc}
              </p>

              {/* Decorative arrow */}
              <ArrowUpRight
                className="absolute bottom-6 right-6 h-5 w-5 text-white/30 transition-all duration-300 group-hover:text-[var(--net-lime)] rtl:left-6 rtl:right-auto rtl:rotate-90"
                aria-hidden
              />

              {/* Hover ring at corner */}
              <span
                className="pointer-events-none absolute -bottom-3 -right-3 h-8 w-8 rounded-full border-2 border-[var(--net-lime)] opacity-0 transition-opacity duration-500 group-hover:opacity-100 rtl:-left-3 rtl:right-auto"
                aria-hidden
              />
              {/* Subtle index outline indicator */}
              <span className="pointer-events-none absolute inset-0 border border-transparent transition-colors duration-500 group-hover:border-white/15" />
              <span className="sr-only">{idx + 1}</span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: Coach Asem feature ───────────────────────── */

function CoachSection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <section className="bg-white">
      <div
        ref={ref}
        className="net-reveal mx-auto grid max-w-[1440px] grid-cols-1 lg:grid-cols-12"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[var(--net-ink)] lg:col-span-5">
          <Image
            src="/landing-pages/netacademy/images/courts.png"
            alt="NET Tennis Academy night courts with ball cart"
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--net-ink)] via-transparent to-transparent" />
        </div>

        <div className="bg-[var(--net-blue)] px-6 py-20 text-white sm:px-12 lg:col-span-7 lg:px-20 lg:py-24">
          <p className="font-net-label mb-6 flex items-center gap-3 text-[10px] text-[var(--net-lime)]">
            <span className="h-px w-10 bg-[var(--net-lime)]" />
            {isAr ? '03 — المدربون' : '03 — Coaching'}
          </p>
          <h2 className="font-net-display text-[2.5rem] leading-[0.9] sm:text-[3.5rem] lg:text-[4.25rem]">
            {isAr ? (
              <>
                على أرض الملعب
                <br />
                <span className="text-[var(--net-lime)]">
                  بقيادة المدرب
                  <br />
                  عاصم.
                </span>
              </>
            ) : (
              <>
                Led on court
                <br />
                <span className="text-[var(--net-lime)]">
                  by Coach
                  <br />
                  Asim.
                </span>
              </>
            )}
          </h2>
          <p className="font-net-body mt-8 max-w-xl text-lg leading-relaxed text-white/85">
            {isAr
              ? 'يركّز مدربونا على التطوير الشامل للاعب. منهج تدريسي واضح، تحليل مستمر، وبيئة تنافسية صحية في كل حصة.'
              : 'Our coaches are focused on overall player development. A clear curriculum, continuous feedback, and a healthy competitive environment in every session.'}
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              href="/coaches"
              className="group inline-flex h-12 items-center gap-2 rounded-full border border-white/40 px-6 text-sm font-medium text-white transition-colors hover:border-[var(--net-lime)] hover:text-[var(--net-lime)]"
            >
              {isAr ? 'تعرف على الفريق' : 'Meet the team'}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: Facilities ───────────────────────── */

function FacilitiesSection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  const facilities = isAr
    ? [
        'سبعة ملاعب بمواصفات دولية',
        'ملعبان بخصوصية كاملة للنساء',
        'الغرفة الداخلية للتمارين والإطالات',
        'غرف تبديل ملابس للجنسين',
        'مراوش',
        'خزائن أمانات للجنسين',
        'شاشة لعرض جميع بطولات التنس',
        'كفي شوب',
        'جلسات داخلية وخارجية',
      ]
    : [
        '7 international courts',
        '2 private women-only courts',
        'Indoor stretching room',
        'Locker rooms',
        'Showers',
        'Security lockers',
        'Live tournaments screen',
        'In-house café',
        'Indoor & outdoor lounges',
      ];

  return (
    <section id="facilities" className="relative overflow-hidden bg-[var(--net-chalk)] py-24 lg:py-32">
      {/* One boomerang shot bouncing across the upper third */}
      <BoomerangShot
        shot={4}
        className="pointer-events-none absolute -left-16 top-12 hidden h-24 w-[700px] text-[var(--net-blue)]/15 lg:block"
        dotColor="var(--net-lime)"
      />

      <div ref={ref} className="net-reveal relative mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-14">
        <div className="mb-16 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-net-label mb-6 flex items-center gap-3 text-[10px] text-[var(--net-blue)]">
              <span className="h-px w-10 bg-[var(--net-blue)]" />
              {isAr ? '04 — المرافق' : '04 — Facilities'}
            </p>
            <h2 className="font-net-display text-[2.5rem] leading-[0.95] text-[var(--net-ink)] sm:text-[3.5rem] lg:text-[5rem]">
              {isAr ? (
                <>
                  كل التفاصيل
                  <br />
                  <span className="text-[var(--net-blue)]">في مكان واحد.</span>
                </>
              ) : (
                <>
                  Everything courtside,
                  <br />
                  <span className="text-[var(--net-blue)]">in one place.</span>
                </>
              )}
            </h2>
          </div>
        </div>

        {/* Asymmetric photo collage — three real images, no repeats */}
        <div className="mb-16 grid grid-cols-12 grid-rows-[260px_260px] gap-3 lg:grid-rows-[300px_300px]">
          {/* Big tile — the courts (the brand's hero shot) */}
          <div className="relative col-span-12 row-span-2 overflow-hidden rounded-2xl bg-[var(--net-ink)] lg:col-span-7">
            <Image
              src="/landing-pages/netacademy/images/courts2.jpeg"
              alt="NET Tennis Academy outdoor courts in Riyadh"
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--net-blue)]/50 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rtl:left-auto rtl:right-6">
              <span className="font-net-display block text-[1.75rem] leading-none text-white">
                {isAr ? 'الملاعب' : 'The courts'}
              </span>
              <span className="font-net-label mt-2 block text-[10px] text-white/80">
                {isAr ? 'سبعة ملاعب · مواصفات دولية' : 'Seven courts · Intl. spec'}
              </span>
            </div>
          </div>

          {/* Top-right — lounge */}
          <div className="relative col-span-12 overflow-hidden rounded-2xl bg-[var(--net-ink)] lg:col-span-5">
            <Image
              src="/landing-pages/netacademy/images/lounge.jpg"
              alt="NET Tennis Academy clubhouse lounge"
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--net-ink)]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 rtl:left-auto rtl:right-6">
              <span className="font-net-display block text-[1.5rem] leading-none text-white">
                {isAr ? 'الجلسات' : 'Lounge'}
              </span>
              <span className="font-net-label mt-1 block text-[10px] text-white/80">
                {isAr ? 'شاشة لعرض بطولات التنس · جلسات داخلية وخارجية' : 'Tournaments screen'}
              </span>
            </div>
          </div>

          {/* Bottom-right — café */}
          <div className="relative col-span-12 overflow-hidden rounded-2xl bg-[var(--net-ink)] lg:col-span-5">
            <Image
              src="/landing-pages/netacademy/images/cafe.jpg"
              alt="NET Tennis Academy café counter"
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--net-ink)]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 rtl:left-auto rtl:right-6">
              <span className="font-net-display block text-[1.5rem] leading-none text-white">
                {isAr ? 'الكافيه' : 'Café'}
              </span>
              <span className="font-net-label mt-1 block text-[10px] text-white/80">
                {isAr ? 'قهوة · معجنات' : 'Coffee · Pastries'}
              </span>
            </div>
          </div>
        </div>

        {/* Facility chips */}
        <ul className="flex flex-wrap gap-2">
          {facilities.map((label) => (
            <li
              key={label}
              className="font-net-body inline-flex items-center gap-2 rounded-full border border-[var(--net-ink)]/15 bg-white px-4 py-2 text-sm text-[var(--net-ink)] transition-colors hover:border-[var(--net-lime)] hover:bg-[var(--net-lime)]"
            >
              <span
                className="h-1.5 w-1.5 rounded-full bg-[var(--net-blue)]"
                aria-hidden
              />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: Big quote ───────────────────────── */

function QuoteSection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <section className="bg-[var(--net-lime)] py-24 lg:py-32">
      <div
        ref={ref}
        className="net-reveal mx-auto max-w-[1100px] px-6 text-center sm:px-10 lg:px-14"
      >
        <span className="font-net-display block text-[6rem] leading-none text-[var(--net-ink)] lg:text-[10rem]">
          “
        </span>
        <p className="font-net-display -mt-8 text-[2rem] leading-[1.05] text-[var(--net-ink)] sm:text-[3rem] lg:text-[4rem]">
          {isAr
            ? 'التنس ليس فقط رياضة. إنه طقس يومي للراحة، للوضوح، وللناس الذين تحبهم.'
            : 'Tennis is not just a sport. It is a daily ritual for relief, clarity, and the people you love playing with.'}
        </p>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: Lazy map ───────────────────────── */

function LazyMap({ src }: { src: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' },
    );

    if (mapRef.current) observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mapRef}
      className="h-[420px] overflow-hidden rounded-2xl bg-[var(--net-chalk)] lg:h-full"
    >
      {isLoaded ? (
        <iframe
          src={src}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="NET Tennis Academy location"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="font-net-label text-[10px] text-[var(--net-ink)]/50">
            Loading map…
          </span>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Section: Location ───────────────────────── */

function LocationSection({ businessInfo }: { businessInfo?: BusinessInfo }) {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  const directionsUrl =
    businessInfo?.location?.googleMapsUrl ?? FALLBACK_DIRECTIONS_URL;
  const embedUrl = resolveEmbedUrl(businessInfo?.location?.googleMapsUrl);
  const phoneRaw = businessInfo?.contact?.phone ?? FALLBACK_PHONE;
  // Normalize for display: ensure leading "+" so the international prefix is shown.
  const phoneDisplay = phoneRaw.startsWith('+')
    ? phoneRaw
    : phoneRaw.startsWith('966')
      ? `+${phoneRaw}`
      : `+966 ${phoneRaw.replace(/^0/, '')}`;
  const phoneHref = phoneRaw.startsWith('+') ? phoneRaw : `+${phoneRaw}`;

  return (
    <section id="location" className="bg-white py-24 lg:py-32">
      <div ref={ref} className="net-reveal mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-14">
        <p className="font-net-label mb-6 flex items-center gap-3 text-[10px] text-[var(--net-blue)]">
          <span className="h-px w-10 bg-[var(--net-blue)]" />
          {isAr ? '05 — الموقع' : '05 — Find us'}
        </p>
        <h2 className="font-net-display mb-16 text-[2.5rem] leading-[0.95] text-[var(--net-ink)] sm:text-[3.5rem] lg:text-[5rem]">
          {isAr ? 'تعال للملعب.' : 'Come courtside.'}
        </h2>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <address className="not-italic">
            <div className="rounded-3xl border border-[var(--net-ink)]/10 bg-[var(--net-chalk)] p-8 lg:p-10">
              <h3 className="font-net-display text-[1.75rem] leading-tight text-[var(--net-ink)]">
                NET Tennis Academy
              </h3>

              <dl className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--net-blue)] text-white"
                    aria-hidden
                  >
                    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                      <path
                        d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </span>
                  <div>
                    <dt className="font-net-label text-[10px] text-[var(--net-ink)]/50">
                      {isAr ? 'العنوان' : 'Address'}
                    </dt>
                    <dd className="font-net-body mt-1 text-base text-[var(--net-ink)]">
                      {isAr ? (
                        <>
                          الدرعية الجديدة، الدرعية 13734
                          <br />
                          الرياض، المملكة العربية السعودية
                        </>
                      ) : (
                        <>
                          PGWF+VG9, Al Diriyah Al Jadidah
                          <br />
                          Diriyah 13734, Saudi Arabia
                        </>
                      )}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--net-blue)] text-white"
                    aria-hidden
                  >
                    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </span>
                  <div>
                    <dt className="font-net-label text-[10px] text-[var(--net-ink)]/50">
                      {isAr ? 'مواعيد العمل' : 'Hours'}
                    </dt>
                    <dd className="font-net-body mt-1 space-y-0.5 text-base text-[var(--net-ink)]">
                      {isAr ? (
                        <>
                          <div>الأحد — الخميس · 6–9 ص &nbsp;·&nbsp; 4 م – 12 ص</div>
                          <div>الجمعة — السبت · 6–10 ص &nbsp;·&nbsp; 4 م – 12 ص</div>
                        </>
                      ) : (
                        <>
                          <div>Sun – Thu · 6–9 AM &nbsp;·&nbsp; 4 PM – 12 AM</div>
                          <div>Fri – Sat · 6–10 AM &nbsp;·&nbsp; 4 PM – 12 AM</div>
                        </>
                      )}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--net-blue)] text-white"
                    aria-hidden
                  >
                    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                      <path
                        d="M5 4h3l2 5-2 1a11 11 0 0 0 6 6l1-2 5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </span>
                  <div>
                    <dt className="font-net-label text-[10px] text-[var(--net-ink)]/50">
                      {isAr ? 'تواصل' : 'Contact'}
                    </dt>
                    <dd className="font-net-body mt-1 text-base">
                      <a
                        href={`tel:${phoneHref}`}
                        className="text-[var(--net-blue)] transition-colors hover:text-[var(--net-blue-deep)]"
                        dir="ltr"
                      >
                        {phoneDisplay}
                      </a>
                    </dd>
                  </div>
                </div>
              </dl>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link
                  href="/book"
                  className="inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-full bg-[var(--net-ink)] px-6 text-sm font-bold text-white transition-colors hover:bg-[var(--net-blue)]"
                >
                  {isAr ? 'احجز ملعب' : 'Book a court'}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-full border border-[var(--net-ink)] px-6 text-sm font-medium text-[var(--net-ink)] transition-colors hover:bg-[var(--net-lime)]"
                >
                  {isAr ? 'الاتجاهات' : 'Directions'}
                  <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" />
                </a>
              </div>
            </div>
          </address>

          <LazyMap src={embedUrl} />
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Section: CTA ───────────────────────── */

function CTASection() {
  const ref = useReveal<HTMLDivElement>();
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <section className="relative overflow-hidden bg-[var(--net-blue)] py-24 text-white lg:py-32">
      <div className="net-court-grid pointer-events-none absolute inset-0 opacity-50" aria-hidden />

      {/* One final boomerang shot — bigger, lower, the brand sign-off */}
      <BoomerangShot
        shot={5}
        className="pointer-events-none absolute -right-24 bottom-10 hidden h-32 w-[820px] text-white/15 lg:block"
        dotColor="var(--net-lime)"
      />

      {/* Diagonal accent shape */}
      <div
        className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rotate-12 bg-[var(--net-lime)]/30 blur-3xl"
        aria-hidden
      />

      <div
        ref={ref}
        className="net-reveal relative mx-auto max-w-[1100px] px-6 text-center sm:px-10 lg:px-14"
      >
        <p className="font-net-label mb-8 text-[10px] text-[var(--net-lime)]">
          {isAr ? 'ابدأ الآن' : 'Start today'}
        </p>
        <h2 className="font-net-display text-[2.75rem] leading-[0.92] sm:text-[4rem] lg:text-[6rem]">
          {isAr ? (
            <>
              نراك
              <br />
              <span className="text-[var(--net-lime)]">في الملعب.</span>
            </>
          ) : (
            <>
              See you on
              <br />
              <span className="text-[var(--net-lime)]">the court.</span>
            </>
          )}
        </h2>
        <p className="font-net-body mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
          {isAr
            ? 'احجز حصتك أو ملعبك الآن. برامجنا التدريبية لجميع الأعمار والمستويات للجنسين.'
            : 'Book a class or a court now. Sessions for men and women, every age, every level.'}
        </p>
        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            href="/book"
            className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[var(--net-lime)] px-10 text-base font-bold text-[var(--net-ink)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
          >
            {isAr ? 'احجز الآن' : 'Book now'}
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
          <a
            href="https://wa.me/966554426646"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-white/40 px-10 text-base font-medium text-white transition-colors hover:border-[var(--net-lime)] hover:text-[var(--net-lime)]"
          >
            {isAr ? 'تواصل واتساب' : 'WhatsApp us'}
          </a>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Page ───────────────────────── */

export default function NetAcademyLandingPage(props: LandingPageProps) {
  // The DB sometimes hands businessInfo through as a partial / unknown shape;
  // narrow it to BusinessInfo or undefined for the location section to consume.
  const businessInfo = props.businessInfo as BusinessInfo | undefined;

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <HeroSection />
      <MarqueeStrip />
      <AboutSection />
      <ProgramsSection />
      <CoachSection />
      <FacilitiesSection />
      <QuoteSection />
      <LocationSection businessInfo={businessInfo} />
      <CTASection />
    </div>
  );
}
